/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import SearchIcon from '@material-ui/icons/Search';
import FilterListIcon from '@material-ui/icons/FilterList';
import {
  EmptyState,
  Link,
  Progress,
  StatusError,
  StatusOK,
  StatusPending,
  StatusWarning,
  WarningPanel,
} from '@backstage/core-components';
import { HowItWorks, HowItWorksStep } from '../common/HowItWorks';
import {
  WatcherPr,
  WatcherState,
  useDependsOnWatcherAccess,
  useDependsOnWatcherApi,
} from './useDependsOnWatcherApi';

const useStyles = makeStyles(theme => ({
  scope: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  scopeDot: { color: theme.palette.text.hint },
  // Org segmented control with a sliding highlight (aurora-tabs style).
  orgTabs: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    padding: 3,
    borderRadius: 999,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.type === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    marginBottom: theme.spacing(1.5),
  },
  orgIndicator: {
    position: 'absolute',
    top: 3,
    bottom: 3,
    left: 0,
    borderRadius: 999,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    transition:
      'transform 220ms cubic-bezier(0.22,1,0.36,1), width 220ms cubic-bezier(0.22,1,0.36,1)',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  orgTab: {
    position: 'relative',
    zIndex: 1,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    font: 'inherit',
    fontSize: '0.8125rem',
    fontWeight: 500,
    padding: theme.spacing(0.5, 1.75),
    borderRadius: 999,
    color: theme.palette.text.secondary,
    transition: 'color 150ms ease',
  },
  orgTabActive: { color: theme.palette.text.primary, fontWeight: 600 },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Fixed height so the text-only "All" pill matches the icon-bearing
    // status pills (StatusOK/Error etc. add an icon that would otherwise make
    // those taller).
    minHeight: 32,
    boxSizing: 'border-box',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0, 1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    fontSize: '0.8125rem',
    color: theme.palette.text.primary,
    transition: 'background-color 150ms ease, border-color 150ms ease',
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  pillActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
    fontWeight: 600,
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  search: { flex: '1 1 260px', minWidth: 220 },
  spacer: { flex: 1 },
  count: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    whiteSpace: 'nowrap',
  },
  tableWrap: { overflowX: 'auto' },
  headCell: { whiteSpace: 'nowrap' },
  headInner: { display: 'inline-flex', alignItems: 'center' },
  filterBtnActive: { color: theme.palette.primary.main },
  popover: { width: 280, padding: theme.spacing(1) },
  popoverList: { maxHeight: 280, overflow: 'auto' },
  popoverFoot: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: theme.spacing(0.5),
  },
  repoCell: { fontFamily: theme.typography.fontFamily, fontWeight: 600, whiteSpace: 'nowrap' },
  prCell: { minWidth: 260 },
  ref: { fontFamily: theme.typography.fontFamily, fontWeight: 600 },
  prTitle: {
    display: 'block',
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    maxWidth: '48ch',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  author: { display: 'inline-flex', alignItems: 'center', gap: theme.spacing(1) },
  avatar: {
    width: 22,
    height: 22,
    fontSize: '0.7rem',
    backgroundColor: theme.palette.grey[500],
  },
  sha: { fontFamily: 'monospace', fontSize: '0.8125rem' },
  when: { color: theme.palette.text.secondary, whiteSpace: 'nowrap', fontSize: '0.8125rem' },
  actions: { display: 'flex', gap: theme.spacing(1), justifyContent: 'flex-end' },
  row: { '&:hover': { backgroundColor: theme.palette.action.hover } },
}));

const howItWorksSteps = (reconcileMinutes: number): HowItWorksStep[] => [
  {
    title: 'Watch',
    body: (
      <>
        Filter by org, repository, author, or status, and sort any column. Blocked
        and pending PRs sort to the top. Each row links to the PR, the author, and
        the head commit.
      </>
    ),
  },
  {
    title: 'Re-check',
    body: (
      <>
        If a PR is stuck on a stale status (its dependency merged but the check
        never refreshed), Re-check re-evaluates it and posts the true status — the
        same thing the {reconcileMinutes}-minute sweep does, on demand.
      </>
    ),
  },
  {
    title: 'Unblock',
    body: (
      <>
        Force-unblock posts a passing status to override the gate for one PR. It
        is momentary — the next PR event re-posts the true status — so use it only
        when you must merge ahead of a dependency.
      </>
    ),
  },
];

const STATUS_LABEL: Record<WatcherState, string> = {
  success: 'passing',
  failure: 'blocked',
  pending: 'pending',
  missing: 'not posted',
};
const cap = (s: string) => s.replace(/^\w/, c => c.toUpperCase());

const StatusMark = ({
  state,
  children,
}: {
  state: WatcherState;
  children: React.ReactNode;
}) => {
  if (state === 'success') return <StatusOK>{children}</StatusOK>;
  if (state === 'failure') return <StatusError>{children}</StatusError>;
  if (state === 'pending') return <StatusPending>{children}</StatusPending>;
  return <StatusWarning>{children}</StatusWarning>;
};

// Operator triage order: blocked first.
const PILL_ORDER: WatcherState[] = ['failure', 'pending', 'missing', 'success'];
type OrderBy = 'repo' | 'pr' | 'author' | 'updated' | 'status';

type Row = WatcherPr & { busy?: boolean };
const keyOf = (p: { owner: string; repo: string; number: number }) =>
  `${p.owner}/${p.repo}#${p.number}`;

// PR/author/avatar URLs arrive from GitHub via the backend, so they're
// attacker-influenceable (a crafted PR/profile). Only render one as a link/src
// when it's an https URL on a github.com host — this fails closed (returns null)
// on javascript:/data: schemes and on off-host phishing/beacon targets, so the
// caller falls back to plain text / initials. Returns the parsed URL string or
// null.
const githubUrl = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  try {
    const u = new URL(raw);
    if (u.protocol !== 'https:') return null;
    const host = u.hostname.toLowerCase();
    if (host !== 'github.com' && !host.endsWith('.githubusercontent.com')) {
      return null;
    }
    return u.toString();
  } catch {
    return null;
  }
};

// Compact relative time for the "Latest Update" column.
const relTime = (iso: string | null): string => {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return 'just now';
  const m = s / 60;
  if (m < 60) return `${Math.floor(m)}m ago`;
  const h = m / 60;
  if (h < 24) return `${Math.floor(h)}h ago`;
  const d = h / 24;
  if (d < 30) return `${Math.floor(d)}d ago`;
  const mo = d / 30;
  if (mo < 12) return `${Math.floor(mo)}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
};

// DevExtreme-style column header filter: a funnel that opens a searchable
// checkbox list of the column's distinct values. Empty selection = no filter.
const HeaderFilter = ({
  title,
  values,
  selected,
  onChange,
  renderValue,
}: {
  title: string;
  values: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  renderValue?: (v: string) => React.ReactNode;
}) => {
  const classes = useStyles();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [q, setQ] = useState('');
  const active = selected.size > 0;
  const shown = useMemo(
    () => values.filter(v => v.toLowerCase().includes(q.trim().toLowerCase())),
    [values, q],
  );
  const toggle = (v: string) => {
    const n = new Set(selected);
    if (n.has(v)) n.delete(v);
    else n.add(v);
    onChange(n);
  };
  return (
    <>
      <Tooltip title={active ? `Filtering ${title.toLowerCase()}` : `Filter ${title.toLowerCase()}`}>
        <IconButton
          size="small"
          aria-label={`Filter ${title}`}
          aria-haspopup="true"
          aria-expanded={Boolean(anchor)}
          className={active ? classes.filterBtnActive : undefined}
          onClick={e => setAnchor(e.currentTarget)}
        >
          <FilterListIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => {
          setAnchor(null);
          setQ('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box className={classes.popover}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            autoFocus
            placeholder={`Search ${title.toLowerCase()}`}
            value={q}
            onChange={e => setQ(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <List dense className={classes.popoverList}>
            {shown.length === 0 ? (
              <ListItem>
                <ListItemText secondary="No matches" />
              </ListItem>
            ) : (
              shown.map(v => (
                <ListItem key={v} button dense onClick={() => toggle(v)}>
                  <ListItemIcon style={{ minWidth: 34 }}>
                    <Checkbox
                      edge="start"
                      size="small"
                      checked={selected.has(v)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText primary={renderValue ? renderValue(v) : v} />
                </ListItem>
              ))
            )}
          </List>
          <Box className={classes.popoverFoot}>
            <Button size="small" disabled={!active} onClick={() => onChange(new Set())}>
              Clear
            </Button>
            <Button size="small" onClick={() => {
          setAnchor(null);
          setQ('');
        }}>
              Done
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

const EMPTY = new Set<string>();

// Live "next reconcile sweep in …" countdown. Isolated so its 1s tick only
// re-renders this label, not the whole (potentially large) table.
const SweepCountdown = ({
  enabled,
  minutes,
  lastSweepAt,
}: {
  enabled: boolean | undefined;
  minutes: number;
  lastSweepAt: number | null | undefined;
}) => {
  const [now, setNow] = useState(() => Date.now());
  const ticking = enabled !== false && Boolean(lastSweepAt);
  useEffect(() => {
    if (!ticking) return undefined; // nothing to count down -> no idle timer
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [ticking]);

  const nextAt = lastSweepAt ? lastSweepAt + minutes * 60_000 : null;
  let label: string;
  if (enabled === false) {
    label = 'reconcile sweep off';
  } else if (!nextAt) {
    label = `reconcile sweep every ${minutes}m (first run pending)`;
  } else if (nextAt - now <= 0) {
    label = 'reconcile sweep due now';
  } else {
    const secs = Math.floor((nextAt - now) / 1000);
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    label = `next reconcile sweep in ${
      m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`
    }`;
  }
  const tip = lastSweepAt
    ? `Sweeps every ${minutes}m · last ran ${new Date(lastSweepAt).toLocaleTimeString()}`
    : `Sweeps every ${minutes}m`;
  return (
    <Tooltip title={tip}>
      <span>{label}</span>
    </Tooltip>
  );
};

// Org filter as a segmented control with a sliding highlight (aurora-tabs
// style). Tabs come from the enrolled orgs, so a newly-enrolled org (e.g.
// m42-games once its config lands) appears here automatically.
const OrgTabs = ({
  orgs,
  value,
  onChange,
}: {
  orgs: string[];
  value: string;
  onChange: (v: string) => void;
}) => {
  const classes = useStyles();
  const ref = useRef<HTMLDivElement | null>(null);
  const [ind, setInd] = useState<{ left: number; width: number }>({ left: 0, width: 0 });
  const tabs = useMemo(() => ['all', ...orgs], [orgs]);

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const measure = () => {
      const el = root.querySelector<HTMLElement>(`[data-org="${value}"]`);
      if (el) setInd({ left: el.offsetLeft, width: el.offsetWidth });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [value, tabs]);

  // role="group" + aria-pressed toggle buttons rather than tab/tablist: these
  // filter the table in place (there is no tabpanel), and native <button>s give
  // the full keyboard path without the WAI-ARIA tab roving-tabindex contract.
  return (
    <div className={classes.orgTabs} ref={ref} role="group" aria-label="Filter by org">
      <span
        className={classes.orgIndicator}
        style={{ transform: `translateX(${ind.left}px)`, width: ind.width }}
        aria-hidden
      />
      {tabs.map(t => (
        <button
          key={t}
          type="button"
          data-org={t}
          aria-pressed={value === t}
          className={`${classes.orgTab} ${value === t ? classes.orgTabActive : ''}`}
          onClick={() => onChange(t)}
        >
          {t === 'all' ? 'All orgs' : t}
        </button>
      ))}
    </div>
  );
};

export const DependsOnWatcher = () => {
  const classes = useStyles();
  const api = useDependsOnWatcherApi();
  const { canWrite } = useDependsOnWatcherAccess();

  const [rows, setRows] = useState<Row[] | undefined>(undefined);
  const [scope, setScope] = useState<{
    orgs: string[];
    repos: string[];
    reconcileEnabled: boolean;
    reconcileMinutes: number;
    lastSweepAt: number | null;
    builtAt: number;
    truncated: boolean;
  }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  // Filters
  const [query, setQuery] = useState('');
  const [orgFilter, setOrgFilter] = useState<string>('all');
  const [repoSel, setRepoSel] = useState<Set<string>>(EMPTY);
  const [authorSel, setAuthorSel] = useState<Set<string>>(EMPTY);
  const [statusSel, setStatusSel] = useState<Set<string>>(EMPTY);

  // Sort. Default 'status' asc = the backend's blocked-first triage order.
  const [orderBy, setOrderBy] = useState<OrderBy>('status');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const sortBy = useCallback(
    (col: OrderBy) => {
      // Read orderBy directly rather than calling setOrder inside the setOrderBy
      // updater (updaters must be pure; the nested setter double-fires under
      // StrictMode and cancels the asc/desc toggle).
      if (orderBy === col) {
        setOrder(o => (o === 'asc' ? 'desc' : 'asc'));
      } else {
        setOrderBy(col);
        setOrder('asc');
      }
    },
    [orderBy],
  );

  const [toast, setToast] = useState<string>();
  const [confirmUnblock, setConfirmUnblock] = useState<Row | undefined>();

  const load = useCallback(
    async (refresh = false) => {
      setLoading(true);
      setError(undefined);
      try {
        const data = await api.listPrs(refresh);
        setRows(data.prs);
        setScope({
          orgs: data.orgs,
          repos: data.repos,
          reconcileEnabled: data.reconcileEnabled,
          reconcileMinutes: data.reconcileMinutes,
          lastSweepAt: data.lastSweepAt,
          builtAt: data.builtAt,
          truncated: data.truncated,
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    load();
  }, [load]);

  const patchRow = useCallback((k: string, patch: Partial<Row>) => {
    setRows(prev => prev?.map(r => (keyOf(r) === k ? { ...r, ...patch } : r)));
  }, []);

  const runAction = useCallback(
    async (row: Row, kind: 'recheck' | 'unblock') => {
      const k = keyOf(row);
      patchRow(k, { busy: true });
      try {
        const res =
          kind === 'recheck'
            ? await api.recheck(row.owner, row.repo, row.number)
            : await api.unblock(row.owner, row.repo, row.number);
        patchRow(k, { busy: false, status: res.status });
        const label = STATUS_LABEL[res.status.state];
        let msg: string;
        if (kind === 'recheck') {
          msg = `${k}: re-checked → ${label}`;
        } else if (res.status.state === 'success') {
          msg = `${k}: unblocked (momentary — a later PR event can re-block it)`;
        } else {
          msg = `${k}: unblock returned ${label}`;
        }
        setToast(msg);
      } catch (e) {
        patchRow(k, { busy: false });
        setToast(`${k}: ${e instanceof Error ? e.message : String(e)}`);
      }
    },
    [api, patchRow],
  );

  // The active org tab scopes the whole summary: pills, counts, funnel value
  // lists and the total all derive from orgScoped, so a repo/author/status from
  // another org isn't offered (or counted) as a choice that matches zero rows.
  const orgScoped = useMemo(
    () =>
      (rows ?? []).filter(
        r => orgFilter === 'all' || r.owner.toLowerCase() === orgFilter.toLowerCase(),
      ),
    [rows, orgFilter],
  );
  const counts = useMemo(() => {
    const c: Record<WatcherState, number> = { success: 0, failure: 0, pending: 0, missing: 0 };
    for (const r of orgScoped) c[r.status.state] += 1;
    return c;
  }, [orgScoped]);

  const distinctRepos = useMemo(
    () => Array.from(new Set(orgScoped.map(r => r.repo))).sort(),
    [orgScoped],
  );
  const distinctAuthors = useMemo(
    () => Array.from(new Set(orgScoped.map(r => r.author))).sort(),
    [orgScoped],
  );
  // Only offer status values that actually occur, blocked-first.
  const statusValues = useMemo(
    () => PILL_ORDER.filter(s => counts[s] > 0).map(String),
    [counts],
  );

  const filtersActive =
    query !== '' ||
    orgFilter !== 'all' ||
    repoSel.size > 0 ||
    authorSel.size > 0 ||
    statusSel.size > 0;

  const clearFilters = useCallback(() => {
    setQuery('');
    setOrgFilter('all');
    setRepoSel(EMPTY);
    setAuthorSel(EMPTY);
    setStatusSel(EMPTY);
  }, []);

  // Switching org resets the per-column funnel selections: their value lists are
  // org-scoped, so a selection from the previous org would keep filtering while
  // being invisible in the new org's funnels (an empty-table dead-end).
  const changeOrg = useCallback((v: string) => {
    setOrgFilter(v);
    setRepoSel(EMPTY);
    setAuthorSel(EMPTY);
    setStatusSel(EMPTY);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (rows ?? []).filter(r => {
      if (statusSel.size > 0 && !statusSel.has(r.status.state)) return false;
      if (repoSel.size > 0 && !repoSel.has(r.repo)) return false;
      if (authorSel.size > 0 && !authorSel.has(r.author)) return false;
      if (orgFilter !== 'all' && r.owner.toLowerCase() !== orgFilter.toLowerCase()) {
        return false;
      }
      if (q && !`${r.repo}#${r.number} ${r.title} ${r.author}`.toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }, [rows, query, orgFilter, repoSel, authorSel, statusSel]);

  const sortKey = useCallback(
    (r: Row): string => {
      if (orderBy === 'repo') return r.repo.toLowerCase();
      if (orderBy === 'pr') return String(r.number).padStart(9, '0');
      if (orderBy === 'author') return r.author.toLowerCase();
      if (orderBy === 'updated') return r.committedAt ?? '';
      return String(PILL_ORDER.indexOf(r.status.state));
    },
    [orderBy],
  );

  const visible = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const ak = sortKey(a);
      const bk = sortKey(b);
      const cmp = ak < bk ? -1 : ak > bk ? 1 : keyOf(a).localeCompare(keyOf(b));
      return order === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortKey, order]);

  if (loading && !rows) return <Progress />;
  if (error && !rows) {
    return (
      <WarningPanel title="Couldn't load the Depends-On watcher" message={error}>
        <Button onClick={() => load(true)} variant="outlined" size="small">
          Retry
        </Button>
      </WarningPanel>
    );
  }

  const total = orgScoped.length;
  const cols = canWrite ? 7 : 6;

  const sortHead = (col: OrderBy, label: string, filter?: React.ReactNode) => (
    <TableCell className={classes.headCell} sortDirection={orderBy === col ? order : false}>
      <span className={classes.headInner}>
        <TableSortLabel
          active={orderBy === col}
          direction={orderBy === col ? order : 'asc'}
          onClick={() => sortBy(col)}
        >
          {label}
        </TableSortLabel>
        {filter}
      </span>
    </TableCell>
  );

  return (
    <Box>
      <HowItWorks
        lead={
          <>
            The cross-repo Depends-On gate posts a <code>depends-on/blocked</code>{' '}
            status on every open PR in the enrolled orgs: green when a PR declares
            no <code>Depends-On:</code> line or all its declared dependencies have
            merged, red while it is still waiting. This page shows where the gate
            is active and lets you act on a stuck PR without leaving Backstage.
          </>
        }
        steps={howItWorksSteps(scope?.reconcileMinutes ?? 30)}
      />

      {/* A refresh failure after the first load: keep the stale list visible but
          say so (the initial-load error path above only fires when !rows). */}
      {error && rows && (
        <Box mb={2}>
          <WarningPanel severity="error" title="Couldn't refresh the list" message={error}>
            <Button onClick={() => load(true)} variant="outlined" size="small">
              Retry
            </Button>
          </WarningPanel>
        </Box>
      )}

      <Box className={classes.scope}>
        <span>Bot active on</span>
        {(scope?.orgs ?? []).map(o => (
          <Chip key={o} size="small" label={o} />
        ))}
        <span>
          {scope?.repos.includes('*') ? 'all repos' : `${scope?.repos.length ?? 0} repo(s)`}
        </span>
        <span className={classes.scopeDot}>&middot;</span>
        <SweepCountdown
          enabled={scope?.reconcileEnabled}
          minutes={scope?.reconcileMinutes ?? 30}
          lastSweepAt={scope?.lastSweepAt}
        />
      </Box>

      {(scope?.orgs.length ?? 0) > 0 && (
        <OrgTabs orgs={scope?.orgs ?? []} value={orgFilter} onChange={changeOrg} />
      )}

      {/* Status pills = summary counts + a quick way to set the status filter. */}
      <Box className={classes.filters}>
        <ButtonBase
          focusRipple
          className={`${classes.pill} ${statusSel.size === 0 ? classes.pillActive : ''}`}
          onClick={() => setStatusSel(EMPTY)}
          aria-pressed={statusSel.size === 0}
        >
          All {total}
        </ButtonBase>
        {PILL_ORDER.map(state => (
          <ButtonBase
            key={state}
            focusRipple
            className={`${classes.pill} ${statusSel.has(state) ? classes.pillActive : ''}`}
            onClick={() =>
              setStatusSel(prev => {
                const n = new Set(prev);
                if (n.has(state)) n.delete(state);
                else n.add(state);
                return n;
              })
            }
            aria-pressed={statusSel.has(state)}
          >
            <StatusMark state={state}>
              {counts[state]} {STATUS_LABEL[state]}
            </StatusMark>
          </ButtonBase>
        ))}
      </Box>

      <Box className={classes.toolbar}>
        <TextField
          className={classes.search}
          size="small"
          variant="outlined"
          placeholder="Search repo, #number, title, or author"
          value={query}
          onChange={e => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {filtersActive && (
          <Button size="small" onClick={clearFilters}>
            Clear filters
          </Button>
        )}
        <span className={classes.spacer} />
        {scope?.builtAt && (
          <Tooltip title={`Snapshot refreshes automatically; last built ${new Date(scope.builtAt).toLocaleTimeString()}`}>
            <span className={classes.count}>updated {relTime(new Date(scope.builtAt).toISOString())}</span>
          </Tooltip>
        )}
        <span className={classes.count}>
          {visible.length === total
            ? `${total} PR${total === 1 ? '' : 's'}`
            : `${visible.length} of ${total}`}
        </span>
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => load(true)}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {scope?.truncated && (
        <Box mb={1}>
          <Typography variant="caption" color="textSecondary">
            Showing the {rows?.length ?? 0} most recently-opened PRs across the
            enrolled orgs; older PRs beyond that cap aren't listed.
          </Typography>
        </Box>
      )}

      <Box className={classes.tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {sortHead(
                'repo',
                'Repository',
                <HeaderFilter
                  title="Repository"
                  values={distinctRepos}
                  selected={repoSel}
                  onChange={setRepoSel}
                />,
              )}
              {sortHead('pr', 'Pull Request')}
              {sortHead(
                'author',
                'Author',
                <HeaderFilter
                  title="Author"
                  values={distinctAuthors}
                  selected={authorSel}
                  onChange={setAuthorSel}
                />,
              )}
              <TableCell className={classes.headCell}>Commit SHA</TableCell>
              {sortHead('updated', 'Latest Update')}
              {sortHead(
                'status',
                'Status',
                <HeaderFilter
                  title="Status"
                  values={statusValues}
                  selected={statusSel}
                  onChange={setStatusSel}
                  renderValue={v => (
                    <StatusMark state={v as WatcherState}>
                      {cap(STATUS_LABEL[v as WatcherState])}
                    </StatusMark>
                  )}
                />,
              )}
              {canWrite && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={cols}>
                  <EmptyState
                    missing="info"
                    title={total === 0 ? 'No open PRs in scope' : 'No PRs match these filters'}
                    description={
                      total === 0
                        ? 'The gate is enrolled but there are no open PRs to show.'
                        : 'Clear the column filters or search to widen the list.'
                    }
                  />
                </TableCell>
              </TableRow>
            ) : (
              visible.map(row => {
                const prUrl = githubUrl(row.url);
                const authorUrl = githubUrl(row.authorUrl);
                const avatarUrl = githubUrl(row.authorAvatar);
                const commitUrl = row.headSha
                  ? `https://github.com/${encodeURIComponent(
                      row.owner,
                    )}/${encodeURIComponent(row.repo)}/commit/${encodeURIComponent(
                      row.headSha,
                    )}`
                  : null;
                return (
                <TableRow key={keyOf(row)} className={classes.row}>
                  <TableCell className={classes.repoCell}>{row.repo}</TableCell>
                  <TableCell className={classes.prCell}>
                    {prUrl ? (
                      <Link to={prUrl}>
                        <span className={classes.ref}>#{row.number}</span>
                      </Link>
                    ) : (
                      <span className={classes.ref}>#{row.number}</span>
                    )}
                    <span className={classes.prTitle}>{row.title}</span>
                  </TableCell>
                  <TableCell>
                    <span className={classes.author}>
                      <Avatar className={classes.avatar} src={avatarUrl ?? undefined} alt="">
                        {row.author.slice(0, 1).toUpperCase()}
                      </Avatar>
                      {authorUrl ? (
                        <Link to={authorUrl}>{row.author}</Link>
                      ) : (
                        row.author
                      )}
                    </span>
                  </TableCell>
                  <TableCell className={classes.sha}>
                    {row.headSha && commitUrl ? (
                      <Link to={commitUrl}>{row.headSha.slice(0, 7)}</Link>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className={classes.when}>
                    {row.committedAt ? (
                      <Tooltip title={new Date(row.committedAt).toLocaleString()}>
                        <span>{relTime(row.committedAt)}</span>
                      </Tooltip>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        row.status.description ||
                        'No depends-on/blocked status posted yet'
                      }
                    >
                      <span>
                        <StatusMark state={row.status.state}>
                          {cap(STATUS_LABEL[row.status.state])}
                        </StatusMark>
                      </span>
                    </Tooltip>
                  </TableCell>
                  {canWrite && (
                    <TableCell align="right">
                      <Box className={classes.actions}>
                        {row.busy ? (
                          <CircularProgress size={20} />
                        ) : (
                          <>
                            <Tooltip title="Re-evaluate and re-post the true status">
                              <Button
                                size="small"
                                startIcon={<RefreshIcon />}
                                onClick={() => runAction(row, 'recheck')}
                              >
                                Re-check
                              </Button>
                            </Tooltip>
                            <Tooltip title="Force this PR to pass (momentary override)">
                              <span>
                                <Button
                                  size="small"
                                  color="secondary"
                                  startIcon={<LockOpenIcon />}
                                  disabled={row.status.state === 'success'}
                                  onClick={() => setConfirmUnblock(row)}
                                >
                                  Unblock
                                </Button>
                              </span>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>

      <Dialog open={Boolean(confirmUnblock)} onClose={() => setConfirmUnblock(undefined)}>
        <DialogTitle>Force-unblock {confirmUnblock && keyOf(confirmUnblock)}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This posts <code>depends-on/blocked = success</code> even though its
            dependencies may not be satisfied. It is a <b>momentary</b> override: the
            next pull-request event (or the reconcile sweep) re-posts the true status
            and can re-block the PR. Prefer <b>Re-check</b> if the dependency has
            actually merged.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUnblock(undefined)}>Cancel</Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => {
              if (confirmUnblock) runAction(confirmUnblock, 'unblock');
              setConfirmUnblock(undefined);
            }}
          >
            Force unblock
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={6000}
        onClose={() => setToast(undefined)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};
